// imports section Start
import React, { useState } from "react";
import { EMPLOYEE_API_03 } from "../../api/ApiModel"; // Add API for verifying old password
import {
  externalgetApi,
  externalpostApi,
} from "../../services/external/externalApis";
import { useSelector } from "react-redux";
import Grid from "@mui/material/Grid";
import Container from "@mui/material/Container";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import Breadcrumbs from "@mui/material/Breadcrumbs";
import Link from "@mui/material/Link";
import AppBar from "@mui/material/AppBar";
import { useNavigate } from "react-router-dom";
import { useTheme } from "@mui/material/styles";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import Navbar from "../../components/common/Navbar";
import { useStyles } from "../../assets/styles/Theme";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import SnackbarMessage from "../../components/common/SnackbarMessage";
import {
  Box,
  TextField,
  IconButton,
  InputAdornment,
  Typography,
  Divider,
} from "@mui/material";
import {
  MSG_NEW_PW_LENGTH,
  MSG_OLD_PW_INC,
  MSG_OLD_PW_REQ,
  MSG_OLD_PW_VER,
  MSG_PW_CHANGE_FAI,
  MSG_PW_CHANGE_SUC,
  MSG_PW_NOT_MATCH,
  MSG_PW_STRENGTH,
} from "../../utils/sys/Messages";
// imports section End

const ChangePassword = () => {
  // Hooks start
  const navigate = useNavigate();
  const theme = useTheme();
  const styles = useStyles(theme);
  const [oldPassword, setOldPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [snackbarOpen, setSnackbarOpen] = useState(false); // Control Snackbar visibility
  const [snackbarMessage, setSnackbarMessage] = useState(""); // Snackbar message
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [showOldPassword, setShowOldPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState("");
  // Hooks end

  // Get user parameters from Redux store
  const SYS_USER = useSelector((state) => state.user) || {};
  const { COMPCODE, USERREFCODE, USERNAME, STOREDATE, SETUPDATE } =
    SYS_USER.USER_PARAM || {};

  // Handlers start

  const handleCloseSnackbar = () => {
    setSnackbarOpen(false);
  };
  // Toggle password visibility
  const handleTogglePasswordVisibility = (setShow) => {
    setShow((show) => !show);
  };

  // Check password strength and enforce validation rules
  const checkPasswordStrength = (password) => {
    if (password.length === 0) return "";
    if (password.length < 8) return "Weak";

    const strongRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[!@#$%^&*])/;
    if (strongRegex.test(password)) return "Strong";

    return "Medium";
  };

  const handleNewPasswordChange = (e) => {
    const value = e.target.value;
    setNewPassword(value);
    setPasswordStrength(checkPasswordStrength(value));
  };

  const validateForm = () => {
    if (oldPassword.length === 0) {
      setSnackbarMessage(MSG_OLD_PW_REQ);
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
      return false;
    }
    if (newPassword.length < 8) {
      setSnackbarMessage(MSG_NEW_PW_LENGTH);
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
      return false;
    }
    if (passwordStrength === "Weak") {
      setSnackbarMessage(MSG_PW_STRENGTH);
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
      return false;
    }
    if (newPassword !== confirmPassword) {
      setSnackbarMessage(MSG_PW_NOT_MATCH);
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
      return false;
    }
    return true;
  };

  // Handle password change process
  const handlePasswordChange = async () => {
    setErrorMessage(""); // Clear any previous errors
    setSuccessMessage("");

    // Validate form before submitting
    if (!validateForm()) return;

    // Verify the old password first
    const isOldPasswordCorrect = await verifyOldPassword();
    if (!isOldPasswordCorrect) return;

    // If old password is verified and passwords match, proceed with updating the password
    await updatePassword();
    setConfirmPassword("");
    setNewPassword("");
    setOldPassword("");
  };
  // Handlers end

  // API calls start

  // API call to verify the old password
  const verifyOldPassword = async () => {
    try {
      const response = await externalgetApi(EMPLOYEE_API_03, "GET", {
        USERREFCODE: String(USERREFCODE),
        OLDPASSWORD: String(oldPassword),
      });

      // Assuming the response contains a direct boolean (true/false)
      if (response === true) {
        setSnackbarMessage(MSG_OLD_PW_VER);
        setSnackbarSeverity("success");
        setSnackbarOpen(true);
        return true;
      } else if (response === false) {
        // setErrorMessage("Old password is incorrect.");
        setSnackbarMessage(MSG_OLD_PW_INC);
        setSnackbarSeverity("error");
        setSnackbarOpen(true);
        return false;
      } else {
        setErrorMessage("Unexpected response from the server.");
        return false;
      }
    } catch (error) {
      console.error("Error verifying old password:", error);
      setErrorMessage("Error verifying old password.");
      return false;
    }
  };

  // API call to update the password
  const updatePassword = async () => {
    try {
      const response = await externalpostApi(EMPLOYEE_API_03, {
        COMPCODE,
        USERREFCODE: String(USERREFCODE),
        USERNAME,
        reason: "Password change",
        password: String(newPassword),
        expdate: SETUPDATE, // Set to appropriate expiration date
        locdate: STOREDATE,
        enuser: USERNAME,
      });

      // Assuming a truthy response indicates success
      if (response) {
        // setSuccessMessage("Password Updated Successfully!");
        setSnackbarMessage(MSG_PW_CHANGE_SUC);
        setSnackbarSeverity("success");
        setSnackbarOpen(true);
        setErrorMessage("");
      } else {
        // setErrorMessage("Password update failed");
        setSnackbarMessage(MSG_PW_CHANGE_FAI);
        setSnackbarSeverity("error");
        setSnackbarOpen(true);
      }
    } catch (error) {
      console.error("Error updating password:", error);
      // setErrorMessage("Password update failed");

      setSnackbarMessage(MSG_PW_CHANGE_FAI);
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    }
  };
  // API calls end

  return (
    <div>
      <Navbar />
      <AppBar sx={styles.AI_CustomAppBar1}>
        <Box sx={styles.AI_HeaderContainer}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Breadcrumbs
                sx={styles.AI_breadcrumbs2}
                position="fixed"
                separator={<NavigateNextIcon fontSize="small" />}
              >
                <Link underline="none">Home</Link>
                <Link underline="none">Profile</Link>
                <Link underline="none">Change Password</Link>
              </Breadcrumbs>
            </Grid>
            <Grid item xs={12} md={6}>
              <Stack
                sx={{
                  ...styles.AI_fixedStack2,
                  direction: "row",
                  justifyContent: "flex-end",
                }}
              ></Stack>
            </Grid>
          </Grid>
        </Box>
      </AppBar>

      <Box sx={styles.cp_changepwd}>
        <Box sx={styles.cp_changepwd_container}>
          <Box mb={1} textAlign={"left"}>
            <Typography variant="title">Change Password</Typography>
          </Box>
          <Divider />
          <Box mt={2}>
            <TextField
              label="Old Password"
              size="small"
              fullWidth
              variant="outlined"
              type={showOldPassword ? "text" : "password"}
              value={oldPassword}
              onChange={(e) => setOldPassword(e.target.value)}
              autoComplete="off"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      sx={styles.input_fields_icon_color}
                      onClick={() =>
                        handleTogglePasswordVisibility(setShowOldPassword)
                      }
                      edge="end"
                    >
                      {showOldPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            {errorMessage && (
              <Typography variant="body2" color="red" pt={"2px"}>
                {errorMessage}
              </Typography>
            )}
          </Box>

          <Box pt={2}>
            <TextField
              label="New Password"
              size="small"
              fullWidth
              variant="outlined"
              type={showNewPassword ? "text" : "password"}
              value={newPassword}
              onChange={handleNewPasswordChange}
              autoComplete="off"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      sx={styles.input_fields_icon_color}
                      onClick={() =>
                        handleTogglePasswordVisibility(setShowNewPassword)
                      }
                      edge="end"
                    >
                      {showNewPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            {/* Password strength indicator */}
            <Typography
              pt={"2px"}
              variant="body2"
              color={
                passwordStrength === "Strong"
                  ? "green"
                  : passwordStrength === "Weak"
                  ? "red"
                  : "orange"
              }
            >
              {passwordStrength && `Password Strength: ${passwordStrength}`}
            </Typography>
          </Box>

          <Box pt={2}>
            <TextField
              label="Confirm Password"
              size="small"
              fullWidth
              variant="outlined"
              type={showConfirmPassword ? "text" : "password"}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              autoComplete="off"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      sx={styles.input_fields_icon_color}
                      onClick={() =>
                        handleTogglePasswordVisibility(setShowConfirmPassword)
                      }
                      edge="end"
                    >
                      {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Box>
          {successMessage && (
            <Typography color="primary">{successMessage}</Typography>
          )}
          <Box display={"flex"} pt={2} gap={2}>
            <Button
              variant="outlined"
              size="medium"
              sx={styles.btn_secondary}
              onClick={() => {
                setOldPassword("");
                setNewPassword("");
                setConfirmPassword("");
                setErrorMessage("");
              }}
            >
              Clear
            </Button>
            <Button
              variant="contained"
              sx={styles.btn_primary}
              onClick={handlePasswordChange}
              size="medium"
            >
              Confirm
            </Button>
          </Box>
        </Box>
      </Box>
      <SnackbarMessage
        open={snackbarOpen}
        handleClose={handleCloseSnackbar}
        message={snackbarMessage}
        severity={snackbarSeverity} // Dynamic severity (success or error)
      />
    </div>
  );
};

export default ChangePassword;
